import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from 'convex/react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../convex/_generated/api'
import { Id } from '../../convex/_generated/dataModel'
import { Header } from '../components/layout/Header'
import { ProfileSection } from '../components/settings/ProfileSection'
import { EditableField } from '../components/settings/EditableField'
import { PhotoUploader } from '../components/settings/PhotoUploader'
import { NotificationToggle } from '../components/settings/NotificationToggle'
import { AllNotificationsDisabledModal } from '../components/settings/AllNotificationsDisabledModal'
import { useToast } from '../components/common/Toast'

/**
 * Settings page - Account Settings feature
 * Allows caregivers to manage their profile, loved one profile,
 * notification preferences, and account actions
 */
export function Settings() {
  const { t, i18n } = useTranslation()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const settings = useQuery(api.settings.getSettings)
  const updateCaregiverProfile = useMutation(api.settings.updateCaregiverProfile)
  const updateLovedOneProfile = useMutation(api.settings.updateLovedOneProfile)
  const updateNotificationPreferences = useMutation(api.settings.updateNotificationPreferences)

  const [showAllDisabledModal, setShowAllDisabledModal] = useState(false)
  const [pendingDisable, setPendingDisable] = useState<'sms' | 'email' | null>(null)
  const [isSavingNotifications, setIsSavingNotifications] = useState(false)

  const containerStyles: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const contentStyles: React.CSSProperties = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem 1rem',
  }

  const titleStyles: React.CSSProperties = {
    fontSize: 'clamp(24px, 4vw, 32px)',
    fontWeight: 700,
    color: '#1a1a1a',
    marginBottom: '2rem',
  }

  const sectionStyles: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  }

  const sectionTitleStyles: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '0.5rem',
  }

  const loadingStyles: React.CSSProperties = {
    textAlign: 'center',
    padding: '4rem 1rem',
    color: '#6b7280',
  }

  const photoSectionStyles: React.CSSProperties = {
    marginBottom: '1.5rem',
    paddingBottom: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  }

  // Language options for select
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'zh-Hant', label: '繁體中文 (Cantonese)' },
  ]

  // Common timezone options
  const timezoneOptions = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'America/Anchorage', label: 'Alaska Time' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong Time' },
    { value: 'Asia/Shanghai', label: 'China Standard Time' },
    { value: 'Asia/Taipei', label: 'Taipei Time' },
    { value: 'Europe/London', label: 'UK Time' },
    { value: 'Europe/Paris', label: 'Central European Time' },
  ]

  // Redirect to onboarding if no caregiver record exists
  useEffect(() => {
    if (settings === null) {
      navigate('/voice-onboarding')
    }
  }, [settings, navigate])

  // Sync i18n language with caregiver's saved preference on load
  useEffect(() => {
    if (
      settings?.caregiver.interfaceLanguage &&
      settings.caregiver.interfaceLanguage !== i18n.language
    ) {
      i18n.changeLanguage(settings.caregiver.interfaceLanguage)
    }
  }, [settings?.caregiver.interfaceLanguage, i18n])

  // Handler for saving caregiver profile fields
  const handleSaveCaregiverField = async (field: string, value: string) => {
    const result = await updateCaregiverProfile({ [field]: value })

    // If language was changed, update the UI language immediately
    if (field === 'interfaceLanguage') {
      i18n.changeLanguage(value)
    }

    return result
  }

  // Handler for saving loved one profile fields
  const handleSaveLovedOneField = async (
    lovedOneId: Id<'lovedOnes'>,
    field: string,
    value: string | null
  ) => {
    const result = await updateLovedOneProfile({ lovedOneId, [field]: value })
    return result
  }

  // Handler for toggling notification preferences
  const handleNotificationToggle = async (type: 'sms' | 'email', enabled: boolean) => {
    if (!settings) return

    // Check if this would disable all notifications
    const otherEnabled =
      type === 'sms' ? settings.caregiver.emailConsent : settings.caregiver.smsConsent
    if (!enabled && !otherEnabled) {
      setPendingDisable(type)
      setShowAllDisabledModal(true)
      return
    }

    await saveNotificationPreference(type, enabled)
  }

  const saveNotificationPreference = async (type: 'sms' | 'email', enabled: boolean) => {
    setIsSavingNotifications(true)
    try {
      const result = await updateNotificationPreferences({
        smsEnabled: type === 'sms' ? enabled : undefined,
        emailEnabled: type === 'email' ? enabled : undefined,
      })
      if (result.success) {
        showToast({ message: t('settings.saved'), type: 'success' })
      } else {
        showToast({ message: t('settings.saveFailed'), type: 'error' })
      }
    } catch {
      showToast({ message: t('settings.saveFailed'), type: 'error' })
    } finally {
      setIsSavingNotifications(false)
    }
  }

  const handleConfirmDisableAll = async () => {
    if (pendingDisable) {
      await saveNotificationPreference(pendingDisable, false)
    }
    setShowAllDisabledModal(false)
    setPendingDisable(null)
  }

  if (settings === undefined) {
    return (
      <div style={containerStyles}>
        <Header />
        <div style={loadingStyles}>{t('common.loading')}</div>
      </div>
    )
  }

  if (settings === null) {
    return (
      <div style={containerStyles}>
        <Header />
        <div style={loadingStyles}>Redirecting to setup...</div>
      </div>
    )
  }

  return (
    <div style={containerStyles}>
      <Header />
      <div style={contentStyles}>
        <h1 style={titleStyles}>{t('settings.title')}</h1>

        {/* Your Profile Section */}
        <ProfileSection
          title={t('settings.yourProfile')}
          description={t('settings.yourProfileDescription')}
        >
          {/* Profile Photo */}
          <div style={photoSectionStyles}>
            <PhotoUploader
              currentPhotoUrl={settings.caregiver.profilePhotoUrl}
              name={settings.caregiver.name || settings.caregiver.email}
              target="caregiver"
            />
          </div>

          {/* Name */}
          <EditableField
            label={t('settings.name')}
            value={settings.caregiver.name || ''}
            placeholder="Enter your name"
            onSave={value => handleSaveCaregiverField('name', value)}
            validate={value => {
              if (value.trim().length < 2) return 'Name must be at least 2 characters'
              if (value.trim().length > 100) return 'Name must be less than 100 characters'
              return null
            }}
          />

          {/* Email (read-only - from Auth0) */}
          <EditableField
            label={t('settings.email')}
            value={settings.caregiver.email}
            disabled={true}
          />

          {/* Notification Email */}
          <EditableField
            label="Notification Email"
            value={settings.caregiver.notificationEmail || ''}
            placeholder="Email for notifications"
            type="email"
            onSave={value => handleSaveCaregiverField('notificationEmail', value)}
            validate={value => {
              if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                return 'Please enter a valid email address'
              }
              return null
            }}
          />

          {/* Phone Number */}
          <EditableField
            label={t('settings.phone')}
            value={settings.caregiver.phoneNumber || ''}
            placeholder="+1 (555) 123-4567"
            type="tel"
            onSave={value => handleSaveCaregiverField('phoneNumber', value)}
          />

          {/* Language */}
          <EditableField
            label={t('settings.language')}
            value={settings.caregiver.interfaceLanguage}
            type="select"
            options={languageOptions}
            onSave={value => handleSaveCaregiverField('interfaceLanguage', value)}
          />

          {/* Timezone */}
          <EditableField
            label={t('settings.timezone')}
            value={settings.caregiver.timezone || ''}
            type="select"
            options={timezoneOptions}
            placeholder="Select timezone"
            onSave={value => handleSaveCaregiverField('timezone', value)}
          />
        </ProfileSection>

        {/* Loved One Profile Section */}
        {settings.lovedOne && (
          <ProfileSection
            title={t('settings.lovedOneProfile')}
            description={t('settings.lovedOneProfileDescription')}
          >
            {/* Profile Photo */}
            <div style={photoSectionStyles}>
              <PhotoUploader
                currentPhotoUrl={settings.lovedOne.profilePhotoUrl}
                name={settings.lovedOne.name}
                target="lovedOne"
                lovedOneId={settings.lovedOne._id}
              />
            </div>

            {/* Name */}
            <EditableField
              label={t('settings.name')}
              value={settings.lovedOne.name}
              placeholder="Enter name"
              required
              onSave={value => handleSaveLovedOneField(settings.lovedOne!._id, 'name', value)}
              validate={value => {
                if (value.trim().length < 2) return 'Name must be at least 2 characters'
                if (value.trim().length > 100) return 'Name must be less than 100 characters'
                return null
              }}
            />

            {/* Phone Number */}
            <EditableField
              label={t('settings.phone')}
              value={settings.lovedOne.phoneNumber}
              placeholder="+1 (555) 123-4567"
              type="tel"
              required
              onSave={value =>
                handleSaveLovedOneField(settings.lovedOne!._id, 'phoneNumber', value)
              }
            />

            {/* Email (optional) */}
            <EditableField
              label={t('settings.email')}
              value={settings.lovedOne.email || ''}
              placeholder="Email (optional)"
              type="email"
              onSave={value =>
                handleSaveLovedOneField(settings.lovedOne!._id, 'email', value || null)
              }
              validate={value => {
                if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                  return 'Please enter a valid email address'
                }
                return null
              }}
            />

            {/* Language */}
            <EditableField
              label={t('settings.language')}
              value={settings.lovedOne.languagePreference}
              type="select"
              options={languageOptions}
              onSave={value =>
                handleSaveLovedOneField(settings.lovedOne!._id, 'languagePreference', value)
              }
            />

            {/* Timezone */}
            <EditableField
              label={t('settings.timezone')}
              value={settings.lovedOne.timezone}
              type="select"
              options={timezoneOptions}
              onSave={value => handleSaveLovedOneField(settings.lovedOne!._id, 'timezone', value)}
            />
          </ProfileSection>
        )}

        {/* Notification Preferences Section */}
        <ProfileSection
          title={t('settings.notifications')}
          description={t('settings.notificationsDescription')}
        >
          <NotificationToggle
            label={t('settings.smsNotifications')}
            description="Receive text messages about your loved one's wellbeing"
            contactInfo={settings.caregiver.phoneNumber || 'No phone number set'}
            enabled={settings.caregiver.smsConsent}
            onChange={enabled => handleNotificationToggle('sms', enabled)}
            disabled={isSavingNotifications || !settings.caregiver.phoneNumber}
          />
          <NotificationToggle
            label={t('settings.emailNotifications')}
            description="Receive email updates about your loved one's wellbeing"
            contactInfo={settings.caregiver.notificationEmail || settings.caregiver.email}
            enabled={settings.caregiver.emailConsent}
            onChange={enabled => handleNotificationToggle('email', enabled)}
            disabled={isSavingNotifications}
          />
        </ProfileSection>

        {/* Danger Zone Section */}
        <section style={{ ...sectionStyles, borderLeft: '4px solid #EF4444' }}>
          <h2 style={{ ...sectionTitleStyles, color: '#EF4444' }}>{t('settings.dangerZone')}</h2>
          <div style={{ color: '#6b7280', fontSize: '14px' }}>
            {/* Danger zone will be implemented in User Story 5 */}
            <p>Account deletion and data export options will appear here.</p>
          </div>
        </section>

        {/* All Notifications Disabled Warning Modal */}
        <AllNotificationsDisabledModal
          isOpen={showAllDisabledModal}
          onClose={() => {
            setShowAllDisabledModal(false)
            setPendingDisable(null)
          }}
          onConfirm={handleConfirmDisableAll}
        />
      </div>
    </div>
  )
}
